import React from "react";
import { Box, Flex, Text, SkuBot, AnimatedSkubotBotIcon } from "@commonsku/design-system";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import type { ChatMessage } from "../../types/skubot";
import { MessageFeedbackActions } from "./MessageFeedbackActions";

export interface ChatMessageItemProps {
  message: ChatMessage;
  isLoading?: boolean;
  showSkuBot?: boolean;
  errorMessage?: string | null;
  onPositiveFeedback?: () => void;
  onNegativeFeedback?: () => void;
}

const markdownComponents: Components = {
  p: ({ children }) => (
    <Text margin="0 0 8px 0" fontSize="md" lineHeight="24px" color="gray.950" whiteSpace="pre-wrap">
      {children}
    </Text>
  ),
  strong: ({ children }) => <strong>{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
  ul: ({ children }) => (
    <Box as="ul" margin="0 0 8px 0" paddingLeft="20px">{children}</Box>
  ),
  ol: ({ children }) => (
    <Box as="ol" margin="0 0 8px 0" paddingLeft="20px">{children}</Box>
  ),
  li: ({ children }) => (
    <Box as="li" fontSize="md" lineHeight="24px" color="gray.950">{children}</Box>
  ),
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer"
      style={{ color: "#0c5c72", textDecoration: "underline" }}>
      {children}
    </a>
  ),
  pre: ({ children }: { children?: React.ReactNode }) => (
    <Box as="pre" bg="#f1f5f9" padding="12px" borderRadius="8px"
      overflowX="auto" margin="0 0 8px 0">
      {children}
    </Box>
  ),
  code: ({ children }: { children?: React.ReactNode }) => (
    <code style={{ fontFamily: "monospace", fontSize: "13px" }}>{children}</code>
  ),
  img: ({ alt }) => (alt ? <Text as="span">[image omitted: {alt}]</Text> : null),

};

export function ChatMessageItem(props: ChatMessageItemProps) {
  const { message, onPositiveFeedback, onNegativeFeedback, isLoading = false, errorMessage = null, showSkuBot = false } = props;
  const isUser = message.role === "user";

  if (errorMessage) {
    return (
      <Flex w="full" padding="12px" justify="flex-end" flexDirection="column">
        <Box
          maxW="100%"
          width={"100%"}
          bg="red.100"
          color="red.700"
          border="1px solid"
          borderColor="red.200"
          borderRadius="8px"
          padding="12px"
        >
          <Text
            margin="0"
            fontSize="sm"
            lineHeight="24px"
            whiteSpace="pre-wrap"
            fontWeight="medium"
            fontFamily="body"
          >
            {errorMessage}
          </Text>
        </Box>
        {showSkuBot && (<Box padding="24px 12px">
          <SkuBot color="skuBlue.600" height="20px" width="36.364px" />
        </Box>
        )}
      </Flex>
    );
  }

  if (isUser) {
    return (
      <Flex w="full" justify="flex-end">
        <Box
          maxW="600px"
          bg="#cffafe"
          color="#0c5c72"
          borderRadius="8px"
          padding="12px"
        >
          <Text
            margin="0"
            fontSize="md"
            lineHeight="24px"
            whiteSpace="pre-wrap"
            wordBreak="break-word"
          >
            {message.content}
          </Text>
        </Box>
      </Flex>
    );
  }

  if (isLoading) {
    return (
      <Box w="full" padding="12px">
        <AnimatedSkubotBotIcon height="40px" width="40px" color="skuBlue.600" />
      </Box>
    );
  }

  return (
    <Box w="full" padding="12px" flexDirection="column">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {message.content}
      </ReactMarkdown>
      {onPositiveFeedback && onNegativeFeedback ? (
        <MessageFeedbackActions
          feedback={message.feedback}
          onPositive={onPositiveFeedback}
          onNegative={onNegativeFeedback}
        />
      ) : null}
      {showSkuBot && (<Box padding="24px 12px">
        <SkuBot color="skuBlue.600" height="20px" width="36.364px" />
      </Box>)}
    </Box>
  );
}
